/* eslint-disable no-console */
// Util compartido de desvinculación de pagos. Extrae la lógica core que vivía acoplada a
// VincularMovimientoModal (handleDesvincularPagoCompleto / handleDesvincularCaja) para que también la
// pueda invocar EstadoCuenta desde las filas de pago del ledger (ver pendiente 9.9).
//
// Patrón fire-and-pray del codebase: cada paso loguea su error pero no aborta el resto
// (preferimos completar la desvinculación aunque queden refs huérfanas que limpiar después).
// El client se recibe por parámetro (default al singleton) para facilitar testing.

import { client } from "../supabase";

// Campos que devuelven un movimiento bancario a 'pendiente', limpiando caja/estado/match_*. Fuente única
// de verdad del reset: la reusan los handlers del modal (VincularMovimientoModal) para no duplicar el
// shape ni olvidar campos (10.1). B3 Fase B paso 2: ya NO se resetean las FK directas
// `factura_sii_emitida_id` / `factura_sii_recibida_id` — nadie las lee (Fase A) ni las escribe (Fase B),
// y el vínculo lo borra el DELETE del puente MBF. R6 (Fase C): las FK escalares `plan_pago_cuota_id` /
// `plan_cobro_cuota_id` fueron DROPEADAS (0 lectores; el vínculo cuota↔pago vive en el libro
// `aplicaciones_saldo.cuota_id` + el puente MBF) → ya no se resetean.
export const RESET_BANCARIO = {
  movimiento_caja_id: null,
  estado: "pendiente",
  procesado_por: null,
  procesado_at: null,
  match_factura_tipo: null,
  match_factura_id: null,
  match_entidad_tipo: null,
  match_entidad_id: null,
  match_entidad_nombre: null,
  match_confianza: null,
  match_razon: null,
};

// Revierte a 'pendiente' las cuotas de plan de PAGOS (facturas recibidas) y de COBROS (facturas
// emitidas) que quedaron atadas a este movimiento bancario. Simétrico al reverso de creditos_cuotas /
// plan_pagos_activos: filtra por movimiento_bancario_id, así solo toca las cuotas de ESTE movimiento —
// inofensivo para los demás pagos. N-tolerante e idempotente (0 filas = el movimiento no pagaba/cobraba
// ninguna cuota, caso normal). Devuelve el conteo revertido por lado. NO traga errores de PostgREST
// (10.170 / 10.177): loguea cada lado por separado sin abortar el resto de la desvinculación
// (patrón fire-and-pray del util). El vínculo cuota↔movimiento vive en dos columnas redundantes
// (plan_*_facturas.movimiento_bancario_id y movimientos_bancarios.plan_*_cuota_id): esta función
// limpia el lado de la cuota; RESET_BANCARIO limpia el lado del movimiento.
export const revertirCuotasFacturaPorMovimiento = async ({ movimientoBancarioId, supabaseClient = client }) => {
  if (!movimientoBancarioId) return { pagos: 0, cobros: 0 };
  let pagos = 0;
  let cobros = 0;

  // Lado PAGOS (facturas recibidas): estado→pendiente + se despega el movimiento y su fecha real.
  try {
    const { data, error } = await supabaseClient
      .from("plan_pagos_facturas")
      .update({ estado: "pendiente", movimiento_bancario_id: null, fecha_pago_real: null })
      .eq("movimiento_bancario_id", movimientoBancarioId)
      .select("id");
    if (error) throw error;
    pagos = (data || []).length;
  } catch (err) {
    console.error("revertirCuotasFacturaPorMovimiento: error revirtiendo plan_pagos_facturas:", err);
  }

  // Lado COBROS (facturas emitidas): espejo exacto — fecha_cobro_real en vez de fecha_pago_real.
  try {
    const { data, error } = await supabaseClient
      .from("plan_cobros_facturas")
      .update({ estado: "pendiente", movimiento_bancario_id: null, fecha_cobro_real: null })
      .eq("movimiento_bancario_id", movimientoBancarioId)
      .select("id");
    if (error) throw error;
    cobros = (data || []).length;
  } catch (err) {
    console.error("revertirCuotasFacturaPorMovimiento: error revirtiendo plan_cobros_facturas:", err);
  }

  return { pagos, cobros };
};

// Desvincula un pago con vinculación directa vía MBF (equivale a handleDesvincularPagoCompleto al borrar
// la última fila): borra MBF, resetea el banco, borra la caja y libera las facturas externas.
export const desvincularPagoMBF = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  try {
    // 1. SELECT filas MBF del movimiento + 2. capturar externas antes del DELETE.
    let externasLiberadas = [];
    try {
      const { data: filas, error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .select("factura_tipo, factura_id")
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
      externasLiberadas = (filas || []).filter((f) => f.factura_tipo === "factura_externa").map((f) => f.factura_id);
    } catch (err) {
      console.error("desvincularPagoMBF: error leyendo filas MBF:", err);
    }

    // 3. DELETE todas las filas MBF del movimiento.
    try {
      const { error } = await supabaseClient
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("movimiento_bancario_id", movimientoBancarioId);
      if (error) throw error;
    } catch (err) {
      console.error("desvincularPagoMBF: error borrando filas MBF:", err);
    }

    // 4. UPDATE banco a pendiente (FKs + match_* limpios).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error reseteando movimiento bancario:", err);
      }
    }

    // 5. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error borrando movimiento de caja:", err);
      }
    }

    // 6. Liberar facturas externas vinculadas (vuelven a pendiente / disponibles para revincular).
    if (externasLiberadas.length > 0) {
      try {
        const { error } = await supabaseClient
          .from("facturas_externas")
          .update({ estado: "pendiente", movimiento_bancario_id: null })
          .in("id", externasLiberadas);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoMBF: error liberando facturas externas:", err);
      }
    }

    // 7. Revertir cuotas de plan de pagos/cobros atadas a este movimiento (huérfano histórico:
    //    ningún path lo hacía). El propio helper loguea sin abortar.
    await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId, supabaseClient });

    return { ok: true, externasLiberadas };
  } catch (err) {
    return { ok: false, error: err };
  }
};

// Desvincula un pago que vive solo en caja (sin MBF) — equivale a handleDesvincularCaja:
// resetea el banco a pendiente (si lo hay) y borra la caja. No toca facturas_externas.
export const desvincularPagoCaja = async ({ movimientoBancarioId, cajaId, supabaseClient = client }) => {
  try {
    // 1. UPDATE banco a pendiente (solo si el pago tiene movimiento bancario asociado).
    if (movimientoBancarioId) {
      try {
        const { error } = await supabaseClient
          .from("movimientos_bancarios")
          .update(RESET_BANCARIO)
          .eq("id", movimientoBancarioId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error reseteando movimiento bancario:", err);
      }
    }

    // 2. DELETE la caja.
    if (cajaId) {
      try {
        const { error } = await supabaseClient.from("movimientos_caja").delete().eq("id", cajaId);
        if (error) throw error;
      } catch (err) {
        console.error("desvincularPagoCaja: error borrando movimiento de caja:", err);
      }
    }

    // 3. Revertir cuotas de plan de pagos/cobros atadas a este movimiento (mismo huérfano que MBF).
    await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId, supabaseClient });

    return { ok: true };
  } catch (err) {
    return { ok: false, error: err };
  }
};
